import { useCallback, useMemo } from 'react'

import {
  getContract,
  padHex,
  prepareContractCall,
  toHex,
  toUnits,
} from 'thirdweb'
import { useSendTransaction } from 'thirdweb/react'

import Client from '../client'
import {
  CHAIN_IDS_TO_TOKEN_MESSENGER_ADDRESSES,
  CHAIN_IDS_TO_USDC_ADDRESSES,
} from '../constants/address'
import { CHAIN_IDS_TO_CHAINS } from '../constants/chain'
import {
  DEFAULT_DECIMALS,
  DEFAULT_DESTINATION_DOMAIN,
  DEFAULT_MIN_FINALITY_THRESHOLD,
  DEFAULT_PADDING,
} from '../constants/token'

import type { SupportedChainId } from '../constants/chain'
import type { Hex } from 'thirdweb'

/**
 * Returns a list of states/actions available from the Token Messenger contract.
 * @param chainId - The chain ID that given contract address is on.
 */
export const useTokenMessenger = (chainId: SupportedChainId) => {
  const client = Client.instance
  const chain = CHAIN_IDS_TO_CHAINS[chainId]
  const contractAddress = CHAIN_IDS_TO_TOKEN_MESSENGER_ADDRESSES[chainId]
  const burnToken = CHAIN_IDS_TO_USDC_ADDRESSES[chainId]

  const contract = useMemo(
    () =>
      getContract({
        client,
        chain,
        address: contractAddress,
      }),
    [chain, client, contractAddress],
  )

  const {
    mutate: sendTransaction,
    data,
    error,
    isPending,
    isSuccess,
  } = useSendTransaction({ payModal: false })

  const depositForBurn = useCallback(
    (amount: number, mintRecipient: Hex) => {
      const transaction = prepareContractCall({
        contract,
        method:
          'function depositForBurn(uint256 amount, uint32 destinationDomain, bytes32 mintRecipient, address burnToken, bytes32 destinationCaller, uint256 maxFee, uint32 minFinalityThreshold)',
        params: [
          toUnits(amount.toString(), DEFAULT_DECIMALS),
          DEFAULT_DESTINATION_DOMAIN, // 1
          padHex(mintRecipient, { size: DEFAULT_PADDING }),
          burnToken, // USDC contract address
          toHex(0, { size: 32 }), // bytes32(0)
          toUnits('0', DEFAULT_DECIMALS), // Set to 0 by default
          DEFAULT_MIN_FINALITY_THRESHOLD, // 2000
        ],
      })
      sendTransaction(transaction)
    },
    [burnToken, contract, sendTransaction],
  )

  return {
    depositForBurn,
    data,
    error,
    isPending,
    isSuccess,
  }
}
